// Angular imports
import { Component, Input, ViewChild, AfterViewInit } from '@angular/core';

// 3rd party imports
import { DxChartComponent } from 'devextreme-angular';
import { plainToClass } from 'class-transformer';

// Component imports
import { MPBaseChartComponent } from '../../../_base/mp-base-chart.component';

// Service imports
import { ScreenService } from '../../../_services/shared-services/screen.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';

// Model imports
import { UserDashboardResultDto } from '../../_models/user-dashboard-result-dto';
import { UserDashboardItemResultDto } from '../../_models/user-dashboard-item-result-dto';
import { UserDashboardProxyListResultDto } from '../../_models/user-dashboard-proxy-list-result-dto';
import { UserService } from '../../../_services/data-services/user.service';

@Component({
    selector: 'app-user-dashboard-bar-chart',
    templateUrl: './user-dashboard-bar-chart.component.html',
    styleUrls: ['./user-dashboard-bar-chart.component.scss']
})
export class UserDashboardBarChartComponent extends MPBaseChartComponent implements AfterViewInit {
    @ViewChild(DxChartComponent, { static: false }) barChart: DxChartComponent;

    @Input() selectedUser: UserDashboardProxyListResultDto;
    @Input() selectedViewOption: any;

    @Input() palette: string;
    @Input() paletteMode: string;
    @Input() chartData: UserDashboardResultDto[];
    @Input() chartTitle: string;
    @Input() chartType = 'Side by Side';

    dashboardChartData: UserDashboardResultDto[];
    dashboardChartPalette: string | string[];
    dashboardChartType = 'bar';
    popupTitle: string;
    filterField: string;
    openValueField = 'open';
    dashboardDetails: UserDashboardItemResultDto[];

    constructor(
        protected readonly screenService: ScreenService,
        protected contextManagerService: ContextManagerService,
        protected helperService: HelperService,
        protected userService: UserService
    ) {
        super(screenService, contextManagerService, helperService, userService);
        this.exportFileName = this.exportFileName.bind(this);
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.bindChartData(this.chartData), 100);
    }

    onChartDrawn(event: any): void {
        if (this.dashboardChartType === 'bar') {
            const series = event.component.getAllSeries();
            series.forEach((s) => {
                const points = s.getAllPoints();
                points.forEach((p) => {
                    const labels = p.getLabels();
                    labels.forEach((l) => {
                        const el = l._group.element;
                        el.setAttribute('pointer-events', 'all');
                        el.onclick = () => {
                            this.onPointClick({ target: { series: s, data: p.data } });
                        };
                    });
                });
            });
        }
    }

    onPointClick(event: any): void {
        const selectedItem: UserDashboardResultDto = plainToClass(UserDashboardResultDto, event.target.data);
        if (!!selectedItem && selectedItem.title !== 'No Data') {
            this.popupTitle = `${selectedItem.title} Module Details`;
            this.filterField = event.target.series.name.toLowerCase();
            this.dashboardDetails = selectedItem.items;
        }
    }

    onAxisLabelClick(event: any): void {
        const selectedItem: UserDashboardResultDto = this.dashboardChartData.find((r) => r.title === event.argument);
        if (!!selectedItem && selectedItem.title !== 'No Data') {
            this.popupTitle = `${selectedItem.title} Module Details`;
            this.filterField = null;
            this.dashboardDetails = selectedItem.items;
        }
    }

    onLegendClick(event: any): void {
        const series = event.target;
        if (series.isVisible()) {
            series.hide();
        } else {
            series.show();
        }
    }

    repaint(event): void {
        event.component.repaint();
    }

    cleanup(): void {
        this.filterField = null;
        this.dashboardDetails = null;
    }

    exportFileName(): string {
        return this.getExportFileName(this.chartTitle);
    }

    private bindChartData(data?: UserDashboardResultDto[]): void {
        if (!data || data.length === 0) {
            const empty = new UserDashboardResultDto();
            empty.title = 'No Data';
            empty.open = 0;
            empty.priority = 0;
            empty.problematic = 0;
            empty.items = [];
            data = [empty];
            this.dashboardChartPalette = ['#ccc'];
        }
        this.dashboardChartType = this.chartType === 'Stacked' ? 'stackedBar' : 'bar';
        this.openValueField = this.chartType === 'Stacked' ? 'actualOpen' : 'open';
        this.dashboardChartData = data;
        setTimeout(() => {
            this.chart = this.barChart;
            this.setChartSize();
        }, 250);
    }
}
